import { logger } from 'src/helpers/logger';

const iceServers = [{ urls: 'stun:stun.l.google.com:19302' }];
const videoParams = { mandatory: { maxFrameRate: 25 } };

function webRTCNormalize() {
  try {
    window.RTCPeerConnection =
      window.RTCPeerConnection || window.webkitRTCPeerConnection || window.mozRTCPeerConnection;
    window.RTCSessionDescription =
      window.RTCSessionDescription ||
      window.webkitRTCSessionDescription ||
      window.mozRTCSessionDescription;
    window.RTCIceCandidate =
      window.RTCIceCandidate || window.mozRTCIceCandidate || window.webkitRTCIceCandidate;
    navigator.getUserMedia =
      navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia;
  } catch (err) {
    logger.warn('webrtcNormalize', err);
  }
}

function isWebRTCAvailable() {
  return !!(
    window &&
    window.RTCPeerConnection &&
    window.RTCSessionDescription &&
    navigator &&
    navigator.getUserMedia
  );
}

function getUserMedia(params, onSuccess, onFailure) {
  navigator.getUserMedia(params, onSuccess, onFailure);
}

function makeConnection({
  onStream,
  onIceCandidate,
  localStream,
  createOffer,
  offer,
  createAnswer,
  audioRequired,
  videoRequired,
  onDisconnect
}) {
  let supported = isWebRTCAvailable();
  if (!supported) return null;
  let connection = new window.RTCPeerConnection({ iceServers });
  connection.ondatachannel = (event) => {
    let dataChannel = event.channel;
    logger.debug('webrtc: connection ondatachannel');
    dataChannel.onopen = () => {
      logger.debug('webrtc: connection dataChannel.onopen');
      dataChannel.onmessage = function (message) {
        logger.debug('webrtc: connection dataChannel received:', message.data);
      };
      logger.debug('webrtc: connection dataChannel sending ping');
      dataChannel.send('ping');
    };
  };
  connection.onsignalingstatechange = function (event) {
    logger.debug('webrtc: connection signaling state change: ', event.target.signalingState);
  };
  connection.oniceconnectionstatechange = function (event) {
    let state = event.target.iceConnectionState;
    logger.debug(
      'webrtc: connection ice connection state change: ',
      event.target.iceConnectionState
    );
    if (state === 'disconnected' && typeof onDisconnect === 'function') onDisconnect(event);
  };
  connection.onicegatheringstatechange = function (event) {
    logger.debug('webrtc: connection ice gathering state change: ', event.target.iceGatheringState);
  };
  if (typeof onStream === 'function') {
    connection.onaddstream = (event) => {
      logger.debug('webrtc: onaddstream', event);
      if (event.stream) onStream(event.stream);
    };
  }
  if (typeof onIceCandidate === 'function') {
    connection.onicecandidate = (event) => {
      let candidate = event && event.candidate;
      logger.debug('webrtc: onicecandidate', candidate);
      if (!candidate) return logger.warn('webrtc: onicecandidate !event.candidate', event);
      onIceCandidate({
        label: candidate.sdpMLineIndex,
        id: candidate.sdpMid,
        candidate: candidate.candidate
      });
    };
  }
  if (localStream) connection.addStream(localStream);
  if (typeof createOffer === 'function') {
    connection.createOffer(
      (offer) => {
        connection.setLocalDescription(
          new window.RTCSessionDescription(offer),
          function () {
            logger.debug('webrtc: createOffer success', offer);
            createOffer(null, offer);
          },
          function (err) {
            logger.warn('webrtc: createOffer err', err);
            createOffer(err, null);
          }
        );
      },
      (err) => logger.warn('webrtc: createOffer error', err),
      {
        mandatory: {
          OfferToReceiveAudio: audioRequired,
          OfferToReceiveVideo: videoRequired
        }
      }
    );
  }
  if (offer) connection.setRemoteDescription(new window.RTCSessionDescription(offer));
  if (typeof createAnswer === 'function') {
    connection.createAnswer(
      function (answer) {
        logger.debug('webrtc: createAnswer success', answer);
        connection.setLocalDescription(new window.RTCSessionDescription(answer));
        createAnswer(null, answer);
      },
      function (err) {
        logger.warn('webrtc: createAnswer err', err.name, err);
        createAnswer(err.name || err, null);
      },
      {
        mandatory: {
          OfferToReceiveAudio: audioRequired,
          OfferToReceiveVideo: videoRequired
        }
      }
    );
  }
  return connection;
}

function getLocalStream(video, callback, audio = true) {
  let getUserMediaParams = {
    audio: audio,
    video: video ? videoParams : undefined
  };
  const getUserMediaSuccess = (localStream) => {
    logger.debug('webrtc: getUserMedia success', localStream);
    callback(null, localStream);
  };
  const getUserMediaError = (err) => {
    logger.warn('webrtc: getUserMedia err', err.name, err);
    callback(err.name || err, null);
  };
  getUserMedia(getUserMediaParams, getUserMediaSuccess, getUserMediaError);
}

function addIceCandidate(connection, icecandidate) {
  connection.addIceCandidate(
    new window.RTCIceCandidate({
      sdpMLineIndex: icecandidate.label,
      sdpMid: icecandidate.id,
      candidate: icecandidate.candidate
    })
  );
}

function setAnswer(connection, answer) {
  connection.setRemoteDescription(new window.RTCSessionDescription(answer));
}

webRTCNormalize();

export const webrtc = {
  getUserMedia,
  isWebRTCAvailable,
  makeConnection,
  getLocalStream,
  addIceCandidate,
  setAnswer
};
